import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef
} from '@angular/material/snack-bar';
import { RouterOutlet } from '@angular/router';
import { MainVerticalMenubarLeftComponent } from '../../components/main-vertical-menubar-left/main-vertical-menubar-left.component';
import { DownloadMsg, NotificationMsg } from '../../models/general.models';
import {
  NotificationMessage,
  ToastPanelData
} from '../../models/notification.model';
import { NotificationService } from '../../services/notification.service';
import { ToastNotificationService } from '../../services/toast-notification.service';
import {
  GlobalNotification,
  ProjectNotification,
  WebSocketNotificationService
} from '../../services/websocket-notification.service';
import { NotificationListComponent } from './components/notification-list/notification-list.component';
import { FrmgSnackBarComponent } from './components/snack-bar/snack-bar.component';

@Component({
  selector: 'frmg-portal-base',
  templateUrl: './portal-base.component.html',
  styleUrls: ['./portal-base.component.scss'],
  imports: [
    MainVerticalMenubarLeftComponent,
    NotificationListComponent,
    RouterOutlet,
    NgClass,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatProgressBar
  ]
})
export class PortalBaseComponent implements OnInit, AfterViewInit {
  uploadDialogState: boolean = false;

  private snackBarRef: MatSnackBarRef<any> = null;
  private currentMessage: NotificationMessage = null;
  private readonly destroyRef = inject(DestroyRef);

  private dismissTimeout: any;
  public notificationsPopups: Array<NotificationMsg> = [];
  public notificationsArray: Array<NotificationMsg> = [];
  public showNotification: boolean = false;
  public totalUnreadNotifications: number = 0;

  constructor(
    public notificationService: NotificationService,
    private viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef,
    private toastNotificationService: ToastNotificationService,
    private snackBar: MatSnackBar,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
    private wsNotificationService: WebSocketNotificationService
  ) {}

  ngOnInit(): void {
    // Subscribe to global notifications
    this.wsNotificationService.globalNotifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (notification: GlobalNotification) => {
          this.handleGlobalNotification(notification);
        },
        error: (err) => {
          console.error('Global notification error:', err);
        }
      });

    // Subscribe to project notifications
    this.wsNotificationService.projectNotifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (notification: ProjectNotification) => {
          this.handleProjectNotification(notification);
        },
        error: (err) => {
          console.error('Project notification error:', err);
        }
      });

    this.fetchNotifications();
  }

  private fetchNotifications() {
    this.notificationService
      .getNotifications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp) => {
        this.notificationService.NotificationsArray = resp?.results || [];
        this.totalUnreadNotifications =
          this.notificationService.NotificationsArray.filter(
            (notification) => !notification.read
          ).length;
        this.notificationsArray = this.notificationService.NotificationsArray;
      });
  }

  private handleGlobalNotification(notification: GlobalNotification): void {
    console.log('Global notification received:', notification);

    // Convert WebSocket notification to NotificationMsg format
    const notificationMsg: NotificationMsg = {
      id: Date.now().toString(),
      message: this.formatGlobalNotificationMessage(notification),
      read: false,
      category: this.getGlobalNotificationCategory(),
      created_at: notification.timestamp,
      updated_at: notification.timestamp,
      team: '',
      user: ''
    };

    this.addNotificationToUI(notificationMsg);
  }

  private handleProjectNotification(notification: ProjectNotification): void {
    console.log('Project notification received:', notification);

    // Convert WebSocket notification to NotificationMsg format
    const notificationMsg: NotificationMsg = {
      id: Date.now().toString(),
      message: this.formatProjectNotificationMessage(notification),
      read: false,
      category: this.getProjectNotificationCategory(notification.event),
      created_at: notification.timestamp,
      updated_at: notification.timestamp,
      team: '',
      user: ''
    };

    this.addNotificationToUI(notificationMsg);
  }

  private addNotificationToUI(notificationMsg: NotificationMsg): void {
    this.notificationsPopups.push(notificationMsg);
    const newArray = this.notificationService.NotificationsArray;
    newArray.push(notificationMsg);
    this.notificationService.NotificationsArray = newArray;
    this.notificationsArray = newArray;
    this.totalUnreadNotifications++;
    this.cd.detectChanges();
  }

  private formatGlobalNotificationMessage(
    notification: GlobalNotification
  ): string {
    // Format global notification message based on event type
    switch (notification.event) {
      default:
        return `Global notification: ${notification.event}`;
    }
  }

  private formatProjectNotificationMessage(
    notification: ProjectNotification
  ): string {
    // Format project notification message based on event type
    switch (notification.event) {
      case 'share_role_change':
        return 'Share role changed in project';
      case 'area_added':
        return 'New area added to project';
      case 'area_edited':
        return 'Area edited in project';
      case 'area_removed':
        return 'Area removed from project';
      case 'project_added':
        return 'New project added';
      case 'project_edited':
        return 'Project edited';
      case 'project_removed':
        return 'Project removed';
      case 'download_completed':
        return 'Download completed';
      case 'report_completed':
        return 'Report completed';
      default:
        return `Project notification: ${notification.event}`;
    }
  }

  private getGlobalNotificationCategory(): any {
    // Return appropriate category for global notifications
    return 'GLOBAL';
  }

  private getProjectNotificationCategory(event: string): any {
    // Return appropriate category for project notifications
    switch (event) {
      case 'share_role_change':
        return 'SHARE';
      case 'area_added':
      case 'area_edited':
      case 'area_removed':
        return 'AREA';
      case 'project_added':
      case 'project_edited':
      case 'project_removed':
        return 'PROJECT';
      case 'download_completed':
        return 'DOWNLOAD';
      case 'report_completed':
        return 'REPORT';
      default:
        return 'PROJECT';
    }
  }

  public newNotifications() {
    return this.notificationsPopups.filter(
      (notification) => !notification.read
    );
  }

  public dismissAll() {
    this.newNotifications().forEach((notification) => {
      this.dismissNotification(notification);
    });
  }

  public dismissNotification(notification: NotificationMsg) {
    notification.read = true;
    this.notificationService
      .readNotification(notification)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this.totalUnreadNotifications--;
      });
  }

  public notificationMsgTransform(str: string) {
    const splitBy = str.split(' by ');
    if (splitBy.length > 1) {
      str = str.replace(
        splitBy[1],
        `<span class="bold-name">${splitBy[1]}</span>`
      );
    }
    const regex = /'([^']+)'/g;
    return str.replace(
      regex,
      (match, p1) => `<span class="bold-name">${p1}</span>`
    );
  }

  ngAfterViewInit(): void {
    this.toastNotificationService
      .getNotification()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notificationMessage) => {
        if (this.shouldDisplayMessage(notificationMessage)) {
          this.currentMessage = notificationMessage;
          this.showSnackBar(notificationMessage);
        }
      });

    this.toastNotificationService
      .getHideNotification()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hideNotification) => {
        if (hideNotification) {
          this.ngZone.run(() => {
            if (this.snackBarRef) {
              this.snackBarRef.dismiss();
            }
          });
        }
      });
  }

  private shouldDisplayMessage(
    notificationMessage: NotificationMessage
  ): boolean {
    if (notificationMessage && notificationMessage.message) {
      if (
        !this.currentMessage ||
        this.currentMessage.message !== notificationMessage.message ||
        this.currentMessage.type !== notificationMessage.type
      ) {
        return true;
      }
    }
    return false;
  }

  private showSnackBar(notificationMessage: NotificationMessage) {
    this.ngZone.run(() => {
      if (this.snackBarRef) {
        this.snackBarRef.dismiss();
      }

      const data: ToastPanelData = {
        notification: notificationMessage,
        parent: this.elementRef,
        panelClass: [],
        destroyToastComponent: () => {}
      };

      const config: MatSnackBarConfig = {
        horizontalPosition: notificationMessage.horizontalPosition || 'center',
        verticalPosition: notificationMessage.verticalPosition || 'top',
        viewContainerRef: this.viewContainerRef,
        duration: notificationMessage.duration || 3000,
        panelClass: notificationMessage.panelClass,
        data
      };
      this.snackBarRef = this.snackBar.openFromComponent(
        FrmgSnackBarComponent,
        config
      );
      if (
        notificationMessage.duration &&
        notificationMessage.duration > 0 &&
        notificationMessage.forceDismiss
      ) {
        if (this.dismissTimeout !== null) {
          clearTimeout(this.dismissTimeout);
          this.dismissTimeout = null;
        }
        this.dismissTimeout = setTimeout(() => {
          if (this.snackBarRef?.instance?.actionButton?._elementRef) {
            this.snackBarRef.instance.actionButton._elementRef.nativeElement.click();
          }
          this.dismissTimeout = null;
        }, notificationMessage.duration);
      }
      this.snackBarRef
        .afterDismissed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.dismissTimeout !== null) {
            clearTimeout(this.dismissTimeout);
            this.dismissTimeout = null;
          }
          this.snackBarRef = null;
          this.currentMessage = null;
        });
    });
  }

  public showNotificationChange(overlay: boolean) {
    this.showNotification = overlay;
    this.cd.detectChanges();
  }

  public hideNotification() {
    this.showNotification = false;
    this.cd.detectChanges();
  }

  public getSubCategoryType = this.notificationService.getSubCategoryType;
  public getCategoryType = this.notificationService.getCategoryType;

  barType(popUp: DownloadMsg) {
    return popUp.total == 0 ? 'indeterminate' : 'determinate';
  }
}
